import { useEffect, useRef, useState } from 'react';
import { AlertDialog, Button, Callout, Card, EmptyState, Modal, NumberField, Spinner, StatCard, StatCardGroup, Switch, Table, Tag, TextField } from '@humanforce/mango-ui-react';
import { ToastContainer, useToast } from '../../../components/ui';
import type { BusinessRecordComplianceConfigurationDto, BusinessRecordComplianceRuleDto } from '../organisationApiTypes';
import { getComplianceRulesConfiguration, resetComplianceRulesToDefaults, saveComplianceRule } from '../complianceRulesApi';

interface ComplianceRulesPanelProps {
  businessRecordId: string;
}

const NO_PARAMETERS_SCHEMA = '{"type":"null"}';
const SEVERITY_TONE: Record<string, 'critical' | 'caution'> = { CannotStartPay: 'critical', Warning: 'caution' };
const IMPACT_STP = 1;
const IMPACT_COSTING = 2;

// "ThresholdValue" -> "Threshold value". Schemas here are always flat
// PascalCase-keyed objects (ComplianceCatalogue's ThresholdSchema/VarianceSchema),
// so this generic humanizer covers every rule without a per-rule label table.
const humanize = (key: string) => {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.length === 0 ? spaced : spaced[0].toUpperCase() + spaced.slice(1).toLowerCase();
};

interface ParameterField {
  name: string;
  numeric: boolean;
  required: boolean;
}

type ParameterDraft = Record<string, number | string | null>;

// VarianceSchema lists its five keys under `required` with no matching
// `properties` entry, so a properties-only field list renders an editor with no
// inputs that can never satisfy the server's required-key check. Union the two
// so every key the schema names gets a field.
const parameterFields = (parameterSchema: string): ParameterField[] => {
  try {
    const parsed = JSON.parse(parameterSchema) as { properties?: Record<string, { type?: string }>; required?: string[] };
    const properties = parsed.properties ?? {};
    const required = parsed.required ?? [];
    return [...new Set([...Object.keys(properties), ...required])].map((name) => ({
      name,
      numeric: properties[name]?.type !== 'string',
      required: required.includes(name),
    }));
  } catch {
    return [];
  }
};

const draftFromValues = (fields: ParameterField[], parameterValuesJson: string | null | undefined): ParameterDraft => {
  let values: Record<string, unknown> = {};
  if (parameterValuesJson) {
    try { values = JSON.parse(parameterValuesJson) as Record<string, unknown>; } catch { values = {}; }
  }
  return Object.fromEntries(fields.map((field) => {
    const value = values[field.name];
    if (field.numeric) return [field.name, typeof value === 'number' ? value : null];
    return [field.name, value === undefined || value === null ? '' : String(value)];
  }));
};

const isBlank = (field: ParameterField, draft: ParameterDraft) =>
  field.numeric ? draft[field.name] === null || draft[field.name] === undefined : String(draft[field.name] ?? '').trim() === '';

// NumberField already filters non-numeric input at the keystroke, so the only
// client-side check left is "a required value is missing" — which
// form-validation.md wants on the field itself, before any backend call.
const validate = (fields: ParameterField[], draft: ParameterDraft): Record<string, string> => Object.fromEntries(
  fields.filter((field) => field.required && isBlank(field, draft)).map((field) => [field.name, `${humanize(field.name)} is required.`]),
);

const draftToJson = (fields: ParameterField[], draft: ParameterDraft): string => JSON.stringify(Object.fromEntries(
  fields.filter((field) => !isBlank(field, draft))
    .map((field) => [field.name, field.numeric ? draft[field.name] : String(draft[field.name]).trim()]),
));

const parameterSummary = (row: BusinessRecordComplianceRuleDto): string => {
  const fields = parameterFields(row.parameterSchema);
  const draft = draftFromValues(fields, row.parameterValuesJson);
  return fields.filter((field) => !isBlank(field, draft)).map((field) => `${humanize(field.name)}: ${String(draft[field.name])}`).join(' · ');
};

const columnsFor = (
  canEdit: boolean,
  isRowBusy: (row: BusinessRecordComplianceRuleDto) => boolean,
  onToggle: (row: BusinessRecordComplianceRuleDto, isEnabled: boolean) => void,
  onEdit: (row: BusinessRecordComplianceRuleDto) => void,
) => [
  {
    field: 'displayName',
    header: 'Rule',
    body: (row: BusinessRecordComplianceRuleDto) => (
      <div className="mgo-stack mgo-gap-xs">
        <span>{row.displayName}</span>
        {row.configurationReviewRequired && <Tag label="Review required" tone="caution" />}
      </div>
    ),
  },
  {
    field: 'severity',
    header: 'Severity',
    body: (row: BusinessRecordComplianceRuleDto) => (
      <Tag label={row.severity === 'CannotStartPay' ? 'Cannot start pay' : 'Warning'} tone={SEVERITY_TONE[row.severity] ?? 'caution'} />
    ),
  },
  {
    field: 'impact',
    header: 'Impact',
    body: (row: BusinessRecordComplianceRuleDto) => (
      <div className="mgo-cluster mgo-gap-xs">
        {(row.impactFlags & IMPACT_STP) !== 0 && <Tag label="STP" tone="blue" />}
        {(row.impactFlags & IMPACT_COSTING) !== 0 && <Tag label="Costing" tone="grey" />}
      </div>
    ),
  },
  {
    field: 'enabled',
    header: 'Enabled',
    body: (row: BusinessRecordComplianceRuleDto) => (
      <Switch
        id={`enable-${row.ruleId}`}
        checked={row.isEnabled}
        disabled={!canEdit || isRowBusy(row)}
        label={`Enable ${row.displayName}`}
        onChange={(checked) => onToggle(row, checked)}
      />
    ),
  },
  {
    field: 'parameters',
    header: 'Parameters',
    body: (row: BusinessRecordComplianceRuleDto) => {
      if (row.parameterSchema === NO_PARAMETERS_SCHEMA) return null;
      const summary = parameterSummary(row);
      return (
        <div className="mgo-stack mgo-gap-xs mgo-align-start">
          {summary !== '' && <span className="mgo-text-xs mgo-color-muted">{summary}</span>}
          <Button label="Set parameters" variant="tertiary" size="small" disabled={!canEdit || isRowBusy(row)} onClick={() => onEdit(row)} />
        </div>
      );
    },
  },
];

export const ComplianceRulesPanel = ({ businessRecordId }: ComplianceRulesPanelProps) => {
  const [rows, setRows] = useState<BusinessRecordComplianceRuleDto[]>([]);
  const [stats, setStats] = useState<BusinessRecordComplianceConfigurationDto['stats'] | null>(null);
  const [meta, setMeta] = useState<BusinessRecordComplianceConfigurationDto['meta'] | null>(null);
  const [loading, setLoading] = useState(true);
  // loading-states.md requires the indicator to be scoped to the region that is
  // actually waiting, so a save is tracked by the category whose Table it
  // belongs to. A reset is a whole-Business-Record action, so `resetting` puts
  // every Table into its loading state instead.
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [editingRule, setEditingRule] = useState<BusinessRecordComplianceRuleDto | null>(null);
  const [draft, setDraft] = useState<ParameterDraft>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Every write returns the whole configuration, so a response that lands after
  // a newer one would roll the table back to older state. Only the newest
  // in-flight request is allowed to apply its result.
  const latestRequest = useRef(0);

  const { addToast, removeToast, toasts } = useToast();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getComplianceRulesConfiguration(businessRecordId)
      .then((result) => {
        if (!mounted) return;
        setRows(result.rules);
        setStats(result.stats);
        setMeta(result.meta);
      })
      .catch((reason) => {
        if (mounted) setError(reason instanceof Error ? reason.message : 'Compliance rules could not be loaded.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [businessRecordId, refresh]);

  const applyResult = (result: BusinessRecordComplianceConfigurationDto, requestId: number) => {
    if (requestId !== latestRequest.current) return;
    setRows(result.rules);
    setStats(result.stats);
    setMeta(result.meta);
  };

  const message = (reason: unknown, fallback: string) => reason instanceof Error ? reason.message : fallback;

  const toggle = async (row: BusinessRecordComplianceRuleDto, isEnabled: boolean) => {
    const requestId = ++latestRequest.current;
    setPendingCategory(row.category);
    try {
      applyResult(await saveComplianceRule(businessRecordId, row.ruleId, { isEnabled, parameterValuesJson: row.parameterValuesJson ?? null }), requestId);
      addToast(`Rule ${isEnabled ? 'enabled' : 'disabled'}: ${row.displayName}`, 'positive');
    } catch (reason) {
      addToast(message(reason, 'The rule could not be saved.'), 'critical', { sticky: true });
    } finally {
      setPendingCategory((current) => current === row.category ? null : current);
    }
  };

  const openEdit = (row: BusinessRecordComplianceRuleDto) => {
    setFieldErrors({});
    setDraft(draftFromValues(parameterFields(row.parameterSchema), row.parameterValuesJson));
    setEditingRule(row);
  };

  const saveParameters = async () => {
    if (!editingRule) return;
    const fields = parameterFields(editingRule.parameterSchema);
    const errors = validate(fields, draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const requestId = ++latestRequest.current;
    setSaving(true);
    try {
      applyResult(await saveComplianceRule(businessRecordId, editingRule.ruleId, {
        isEnabled: editingRule.isEnabled,
        parameterValuesJson: draftToJson(fields, draft),
      }), requestId);
      addToast(`Parameters saved for ${editingRule.displayName}`, 'positive');
      setEditingRule(null);
    } catch (reason) {
      addToast(message(reason, 'The parameters could not be saved.'), 'critical', { sticky: true });
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    const requestId = ++latestRequest.current;
    setResetting(true);
    try {
      applyResult(await resetComplianceRulesToDefaults(businessRecordId), requestId);
      setShowResetConfirm(false);
      addToast('Compliance rules reset to defaults', 'positive');
    } catch (reason) {
      addToast(message(reason, 'The compliance rules could not be reset.'), 'critical', { sticky: true });
    } finally {
      setResetting(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div aria-busy="true" aria-label="Loading Compliance rules" className="mgo-cluster mgo-justify-center mgo-border-neutral" style={{ minHeight: '15rem' }}>
        <div style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner /></div>
      </div>
    );
  }

  if (error) {
    return (
      <Callout tone="critical" title="Compliance rules could not be loaded" action={<Button label="Retry" size="small" variant="secondary" onClick={() => setRefresh((value) => value + 1)} />}>
        {error}
      </Callout>
    );
  }

  const canEdit = meta?.actions.canEdit ?? false;
  const canReset = meta?.actions.canReset ?? false;
  const categories = Array.from(new Set(rows.map((row) => row.category)));
  const isCategoryBusy = (category: string) => resetting || pendingCategory === category;
  const columns = columnsFor(canEdit, (row) => isCategoryBusy(row.category), (row, isEnabled) => void toggle(row, isEnabled), openEdit);
  const editingFields = editingRule ? parameterFields(editingRule.parameterSchema) : [];

  return (
    <div className="mgo-stack mgo-gap-l">
      <Callout
        tone="info"
        title="Compliance rules and Pay Run exceptions"
        action={<Button label="Reset to defaults" variant="destructive" disabled={!canReset || resetting || pendingCategory !== null} onClick={() => setShowResetConfirm(true)} />}
      >
        Enabled rules run on every Pay Run for this Business Record and appear there as "exceptions". Rule definitions, evaluation, severity, and "Cannot start pay" logic are owned and maintained by the Payroll Processing Engine — this tab only controls which rules apply to this Business Record.
      </Callout>

      <StatCardGroup>
        <StatCard label="Enabled rules" value={stats?.enabledCount ?? 0} trailingText={`of ${stats?.totalCount ?? 0}`} />
        <StatCard label="Categories" value={stats?.categoryCount ?? 0} />
        <StatCard label="Cannot start pay" value={stats?.cannotStartPayCount ?? 0} tone={(stats?.cannotStartPayCount ?? 0) > 0 ? 'red' : 'neutral'} description="Blocks a Pay Run from starting" />
        <StatCard label="Impacts STP" value={stats?.impactsStpCount ?? 0} tone="blue" />
      </StatCardGroup>

      {!canEdit && (
        <Callout tone="neutral" title="Compliance rules are read-only">
          {meta?.businessRecordInactive
            ? "This Business Record is inactive, so rules can't be enabled, disabled, or reset."
            : "Your role doesn't include permission to change compliance rules."}
        </Callout>
      )}

      {categories.length === 0
        ? <Card><EmptyState title="No compliance rules available" description="The rule catalogue returned no configurable rules for this Business Record. Check back once the catalogue has been published." /></Card>
        : categories.map((category) => (
          <Card key={category} title={category}>
            <Table
              value={rows.filter((row) => row.category === category)}
              columns={columns}
              scrollable
              loading={isCategoryBusy(category)}
              testId={`compliance-rules-table-${category}`}
            />
          </Card>
        ))}

      {editingRule && (
        <Modal
          visible
          header={`${editingRule.displayName} parameters`}
          onHide={() => setEditingRule(null)}
          footer={(
            <>
              <Button label="Cancel" variant="secondary" disabled={saving} onClick={() => setEditingRule(null)} />
              <Button label="Save changes" variant="primary" loading={saving} onClick={() => void saveParameters()} />
            </>
          )}
        >
          <div className={editingFields.length > 1 ? 'mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m' : 'mgo-stack mgo-gap-l'}>
            {editingFields.map((field) => field.numeric ? (
              <NumberField
                key={field.name}
                id={`param-${field.name}`}
                label={humanize(field.name)}
                required={field.required}
                value={draft[field.name] as number | null}
                status={fieldErrors[field.name] ? 'critical' : undefined}
                statusText={fieldErrors[field.name]}
                onChange={(value) => setDraft((current) => ({ ...current, [field.name]: value }))}
              />
            ) : (
              <TextField
                key={field.name}
                id={`param-${field.name}`}
                label={humanize(field.name)}
                required={field.required}
                value={String(draft[field.name] ?? '')}
                status={fieldErrors[field.name] ? 'critical' : undefined}
                statusText={fieldErrors[field.name]}
                onChange={(event) => setDraft((current) => ({ ...current, [field.name]: event.target.value }))}
              />
            ))}
          </div>
        </Modal>
      )}

      {showResetConfirm && (
        <AlertDialog
          visible
          header="Reset to defaults?"
          confirmLabel="Reset all rules"
          confirmLoading={resetting}
          onConfirm={() => void confirmReset()}
          onCancel={() => setShowResetConfirm(false)}
        >
          Every rule on this Business Record goes back to its platform default enabled state, and any parameter values you've set are cleared. This can't be undone.
        </AlertDialog>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};
